/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import * as Sentry from '@sentry/react';
import { everspanAndSCORStatesMapToPolicyType } from '@ourbranch/lookups';
import { useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import { policyTypes } from '@ourbranch/policy-types';
import isEqual from 'lodash-es/isEqual';

import { useStore } from 'core/store';
import { Loading } from 'core';
import { removeQueryParamsFromUrl } from 'core/helpers/url-utils';
import { AuthContext } from 'core/components/auth';
import ErrorDialog from 'core/components/error-dialog/error-dialog';
import LeavePageDialog from 'core/components/leave-page-dialog';
import Section from 'core/components/section';
import { useToast } from 'core/components/toast/context';
import { getPolicyStatus, PolicyStatus, getPolicyChange } from 'core/helpers/policy-status';
import { PolicyTabs, PolicyModalTypes } from 'core/store/account-store/policies-store/policy-store/policy-store';
import { track } from 'core/helpers/analytics';
import useSession from 'core/hooks/use-session';
import { getErrorData } from 'common/constants/get-error-data';
import { haveAnyOfThisCarsSymbolsChanged } from 'common/helpers/car-symbols-helpers';
import { DisableProvider } from 'common/disabled-context';
import * as fragments from './policy.query.fragments';
import PolicyDetails from './details';
import Incidents from './incidents';
import { PaymentTab } from './payment-tab';
import PolicySettings from './policy-settings';
import Preview from './preview';
import Documents from '../documents/documents';
import { PolicyNotifications } from './policy-notifications';
import RenewalNotice from './renewal-notice';
import { WarningModal } from './warning-modal';
import CancelledPolicyFooter from './footer/cancelled-policy-footer';
import useStyles from './policy.styles';
import { getChangesForApproval } from './preview/helpers';
import { SubmittedForApprovalModal } from './submitted-for-approval-modal';
import { PolicyPreviewExpiredModal } from './policy-preview-expired-modal';
import { useQueryParams } from 'core/hooks/useQueryParams';

const GET_POLICY_PREVIEW = gql`
  query getPolicyPreview($policyId: ID!, $previewId: ID!) {
    getPolicyPreview(policyId: $policyId, previewId: $previewId) {
      ...PolicyPreviewResponse
    }
  }
  ${fragments.PolicyPreviewResponse}
`;

const formatAdditionalParties = ({ additionalParties }) => {
  if (!Array.isArray(additionalParties)) {
    return additionalParties;
  }
  return additionalParties.map((party) => {
    if (party.address && !party.address.address) {
      return {
        ...party,
        address: {
          address: '',
          address2: party.address?.address2 || '',
          city: party.address?.city || '',
          state: party.address?.state || '',
          zip: party.address?.zip || ''
        }
      };
    }
    return party;
  });
};

const checkCanReinstatePolicy = ({
  cancelReason,
  policyStatus,
  session,
  canUserRescindOrReinstateGSNICPolicy,
  isCancelledGSNICPolicy
}) => {
  if (policyStatus !== PolicyStatus.Cancelled) {
    return false;
  }

  // gsnic logic
  if (isCancelledGSNICPolicy) {
    if (cancelReason === 'CNPY') {
      return session.canReinstateCNPY && canUserRescindOrReinstateGSNICPolicy;
    }
    return session.canRescindOrReinstateGSNICPolicy;
  }

  // bix logic
  if (cancelReason === 'CNPY') {
    return session.canReinstateCNPY;
  }

  return session.canReinstate;
};

const checkIfUserCanReinstateRescindGSNICPolicy = ({ isBix, nonRenewReason, session }) => {
  return !isBix
    ? nonRenewReason === 'NRSC'
      ? session?.canReinstate || session?.canRescindCancellation
      : session?.canRescindOrReinstateGSNICPolicy
    : false;
};

const checkCanRescindPolicy = ({
  isCancelledGSNICPolicy,
  canUserRescindOrReinstateGSNICPolicy,
  session,
  policyStatus
}) => {
  return isCancelledGSNICPolicy
    ? canUserRescindOrReinstateGSNICPolicy && policyStatus === PolicyStatus.InCancellation
    : session.canRescindCancellation && policyStatus === PolicyStatus.InCancellation;
};

const friendlyErrorMessage = (message) => {
  if (message?.toLowerCase()?.includes('only verified bank accounts can be used')) {
    return `Unable to make the ACH account the primary method of payment yet, because this ACH account has not yet been verified. To verify it, go to the member's page and confirm the microdeposits with the member.`;
  }
  return message;
};

const Policy = ({ history }) => {
  const classes = useStyles();
  const toast = useToast();
  const session = useContext(AuthContext);
  const [promptTab, setPromptTab] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [error, setError] = useState({});
  const [preview, setPreview] = useState(null);
  const [action, setAction] = useState('');
  const [reasons, setReasons] = useState({});
  const [renewalPolicyId, setRenewalPolicyId] = useState(null);
  const [remountKey, setRemountKey] = useState(0);
  const [disableLeavePageModal, setDisableLeavePageModal] = useState(false);

  const {
    account: {
      fetchFullAccountAndPolicy,
      updateAccount,
      id: accountId,
      mailingAddress,
      fname,
      lname,
      email,
      phoneNumber,
      sendEventAsMember,
      policies: {
        notifications,
        policy: {
          policy,
          loading,
          setChanged,
          changed,
          changePolicy,
          changeSegment,
          addSegment,
          previewPolicyChangeScrubIncidents,
          getRenewalPolicyId,
          confirm,
          submitChangesForApproval,
          sendApprovedRequestedChangesCommunication,
          currentTab,
          setCurrentTab,
          cancelled,
          nonRenewReason,
          cancelReason,
          revertPreview,
          revertLoading,
          activePolicyModal,
          setActivePolicyModal,
          closePolicyModal
        }
      }
    }
  } = useStore();

  const { isAgency, user, canApproveRequestedPIPChanges } = useSession();
  const { previewId: previewIdFromQueryParams } = useQueryParams();

  const changesForApprovalConfig = getChangesForApproval({ policy, preview, session });

  const {
    data: previewData,
    loading: policyPreviewLoading,
    error: policyPreviewError
  } = useQuery(GET_POLICY_PREVIEW, {
    variables: { policyId: policy?.id, previewId: previewIdFromQueryParams },
    skip: !previewIdFromQueryParams || !policy?.id || !canApproveRequestedPIPChanges
  });

  useEffect(() => {
    // Only load preview from previewIdFromQueryParams for processing users
    if (!canApproveRequestedPIPChanges && previewIdFromQueryParams) {
      removeQueryParamsFromUrl(history);
      return;
    }

    // Load preview data when fetched
    if (previewIdFromQueryParams && !policyPreviewLoading && previewData) {
      setPreview(previewData.getPolicyPreview);
      setActivePolicyModal(PolicyModalTypes.PREVIEW);
      setCurrentTab(PolicyTabs.DETAILS);
      setLoadingPreview(false);
    }

    if (previewIdFromQueryParams && policyPreviewLoading) {
      setLoadingPreview(true);
    }

    if (previewIdFromQueryParams && policyPreviewError) {
      setActivePolicyModal(PolicyModalTypes.PREVIEW_LINK_EXPIRED);
    }
  }, [
    previewData,
    policyPreviewLoading,
    previewIdFromQueryParams,
    policyPreviewError,
    setPreview,
    setActivePolicyModal,
    setLoadingPreview,
    setError
  ]);

  useEffect(() => {
    if (revertLoading) {
      setLoadingPreview(true);
      setActivePolicyModal(PolicyModalTypes.PREVIEW);
    } else if (revertPreview) {
      setLoadingPreview(false);
      setPreview(revertPreview);
    }
  }, [revertPreview, revertLoading, setLoadingPreview, setPreview, setActivePolicyModal]);

  useEffect(() => {
    if (currentTab !== PolicyTabs.DETAILS && preview) {
      removeQueryParamsFromUrl(history);
    }
  }, [currentTab]);

  const isLoading = loading || !policy || policyPreviewLoading;
  const policyStatus = isLoading ? null : getPolicyStatus(policy);

  const cancelledOrInCancellation =
    policyStatus === PolicyStatus.InCancellation || policyStatus === PolicyStatus.Cancelled;

  // Disable navigation prompt for cancelled policies showing reinstate/rescind footers
  useEffect(() => {
    if (cancelledOrInCancellation) {
      setDisableLeavePageModal(true);
    } else {
      setDisableLeavePageModal(false);
    }
  }, [cancelledOrInCancellation]);

  const isCancelledGSNICPolicy =
    (cancelled || cancelledOrInCancellation) &&
    !policy.isBix &&
    everspanAndSCORStatesMapToPolicyType[policy.state]?.includes(policy.policyType);

  // there is some sort of issue using session or destructuring session before loading is fully done, so have to do this ternary
  const canUserRescindOrReinstateGSNICPolicy = isLoading
    ? false
    : checkIfUserCanReinstateRescindGSNICPolicy({
        isBix: policy?.isBix,
        nonRenewReason,
        session
      });

  // there is some sort of issue using session or destructuring session before loading is fully done, so have to do this ternary
  const allowReinstatement = isLoading
    ? false
    : checkCanReinstatePolicy({
        cancelReason,
        policyStatus,
        session,
        canUserRescindOrReinstateGSNICPolicy,
        isCancelledGSNICPolicy
      });

  // there is some sort of issue using session or destructuring session before loading is fully done, so have to do this ternary
  const allowRescind = isLoading
    ? false
    : checkCanRescindPolicy({
        isCancelledGSNICPolicy,
        canUserRescindOrReinstateGSNICPolicy,
        session,
        policyStatus
      });

  const setLoadingState = () => {
    setLoadingPreview(true);
    setActivePolicyModal(PolicyModalTypes.PREVIEW);
    setError({});
    setPreview(null);
  };

  const closeModal = () => {
    closePolicyModal();
    // removeQueryParamsFromUrl();
  };

  const handleError = ({ error }, values) => {
    Sentry.captureException(
      JSON.stringify({
        res: error,
        ...values
      })
    );
    const { message, ...errorRest } = error;
    const defaultMessages = message
      ? [message]
      : [
          'Sorry about that. It looks like our systems are not able process a preview for the changes.',
          'Please try again'
        ];

    const defaultError = {
      messages: defaultMessages,
      title: 'Settings preview error',
      label: 'Try Again',
      tryAgain: error?.tryAgain || handleChangePolicy.bind(this, values),
      ...errorRest
    };

    let errorToDisplay;

    if (error?.networkError) {
      errorToDisplay = {
        messages: [
          'Our servers are not responding.',
          'You might have lost your internet connection, please check it.',
          'If not, just refresh this page.'
        ],
        label: 'Refresh',
        title: 'Whoops!',
        tryAgain: window.location.reload.bind(window.location)
      };
    }

    if (error?.message?.includes('Hardship moratorium zip code')) {
      errorToDisplay = {
        messages: [error.message],
        title: 'Hardship moratorium detected',
        label: 'Close',
        onClick: () => {
          setError({});
          closeModal();
        }
      };
    }

    if (error?.message?.includes('Mid-term Date Past Expiration Date')) {
      errorToDisplay = {
        messages: ['The transaction date is past the expiration date. Not enough advance notice days.'],
        title: 'Mid-term Date Past Expiration Date',
        label: 'Close',
        onClick: () => {
          setError({});
          closeModal();
        }
      };
    }

    if (!errorToDisplay) {
      errorToDisplay = defaultError;
    }
    setError(errorToDisplay);
    closePolicyModal();
    setAction(null);
  };

  const handleChangePolicy = useCallback(
    async (values) => {
      const currentPolicy = getPolicyStatus(policy);
      const newPolicy = getPolicyStatus(values);
      const action = getPolicyChange(currentPolicy, newPolicy);
      setLoadingState();

      // clear out cancel reason on reinstates and rescinds
      if ((allowRescind || allowReinstatement) && (action === 'reinstate' || action === 'rescind')) {
        values.cancelReason = 'NA';
        if (allowReinstatement && action === 'reinstate') {
          values.nonRenewReason = 'NA';
        }
      }
      const res = await changePolicy({
        ...values,
        additionalParties: formatAdditionalParties(values)
      });
      if (res.ok) {
        const { preview, reasons } = res;

        preview.userSelectedEndDate = values?.transactionDate || values.endDate;
        setReasons(reasons);
        setPreview(preview);
        setAction(action);
      } else {
        handleError(res, { values });
        closeModal();
        setAction(null);
      }
      setLoadingPreview(false);
    },

    [policy, changePolicy, allowReinstatement, allowRescind]
  );

  const handleSegmentChange = useCallback(
    async (segment, changeType, initialValues) => {
      setLoadingState();
      setAction(null);
      const segmentChangeFunction = changeType === 'edit_change' ? changeSegment : addSegment;
      const policyId = policy.id;
      const hasSymbols = (car) => haveAnyOfThisCarsSymbolsChanged(car, initialValues.cars);
      if (segment.cars?.some((car) => hasSymbols(car))) {
        segment.cars.forEach((car) => {
          if (hasSymbols(car)) {
            car.manuallyAddedSymbols = true;
            car.symbolMake = car.symbolMake?.toUpperCase();
            car.symbolModel = car.symbolModel?.toUpperCase();
            car.symbolStyle = car.symbolStyle?.toUpperCase();
            car.symbolAux = car.symbolAux?.toUpperCase();
            car.symbolPGS = car.symbolPGS?.toUpperCase() || ' ';
          }
        });
      }

      if (segment.umbrellaCoverage) {
        /**
         * This is a temporary fix until the use case is properly handled
         * @TODO: Support free input addresses for rental properties
         */
        const { rentalPropertyAddresses, otherPropertyAddresses } = segment.umbrellaCoverage || {};

        segment.umbrellaCoverage.rentalPropertyAddresses =
          rentalPropertyAddresses?.filter((address) => typeof address === 'object') || [];
        segment.umbrellaCoverage.otherPropertyAddresses =
          otherPropertyAddresses?.filter((address) => typeof address === 'object') || [];
      }

      const revisedSegment = { ...segment };
      if (policy.policyType !== 'A') {
        delete revisedSegment.drivers;
      }
      const res = await segmentChangeFunction(policyId, revisedSegment);
      if (res.ok) {
        const { preview } = res;
        setPreview(preview);
        if (preview.warning) {
          setActivePolicyModal(PolicyModalTypes.WARNING);
        }

        if (policy.policyType === policyTypes.Auto) {
          // Check if any drivers' PII (dateOfBirth or driversLicenseNumber) has changed & fire segment events
          const driversWithPIIChanges = [];

          if (!segment.drivers || !initialValues.drivers) {
            // Log if drivers data is missing to help debug issues
            Sentry.captureMessage('Driver PII tracking skipped - missing driver data', {
              level: 'info',
              extra: {
                policyId: policy.id,
                hasSegmentDrivers: !!segment.drivers,
                hasInitialDrivers: !!initialValues.drivers,
                segmentDriversCount: segment.drivers?.length || 0,
                initialDriversCount: initialValues.drivers?.length || 0
              }
            });
          }

          if (segment.drivers && initialValues.drivers) {
            const normalizeString = (str) => (str || '').trim().toLowerCase();
            segment.drivers.forEach((segmentDriver) => {
              const initialDriver = initialValues.drivers.find(
                (initial) =>
                  normalizeString(initial.firstName) === normalizeString(segmentDriver.firstName) &&
                  normalizeString(initial.middleName) === normalizeString(segmentDriver.middleName) &&
                  normalizeString(initial.lastName) === normalizeString(segmentDriver.lastName)
              );

              // Log if driver matching fails
              if (!initialDriver) {
                Sentry.captureMessage('Driver PII tracking - driver match failed', {
                  level: 'info',
                  extra: {
                    policyId: policy.id,
                    segmentDriverName:
                      `${segmentDriver.firstName} ${segmentDriver.middleName || ''} ${segmentDriver.lastName}`.trim(),
                    initialDriverNames: initialValues.drivers.map((d) =>
                      `${d.firstName} ${d.middleName || ''} ${d.lastName}`.trim()
                    )
                  }
                });
              }

              if (
                initialDriver &&
                (normalizeString(segmentDriver.dateOfBirth) !== normalizeString(initialDriver.dateOfBirth) ||
                  normalizeString(segmentDriver.driversLicenseNumber) !==
                    normalizeString(initialDriver.driversLicenseNumber))
              ) {
                driversWithPIIChanges.push({
                  driver: segmentDriver,
                  changes: {
                    dateOfBirth:
                      normalizeString(segmentDriver.dateOfBirth) !== normalizeString(initialDriver.dateOfBirth),
                    driversLicenseNumber:
                      normalizeString(segmentDriver.driversLicenseNumber) !==
                      normalizeString(initialDriver.driversLicenseNumber)
                  }
                });
              }
            });
          }

          if (driversWithPIIChanges.length) {
            // Log that we're about to send events
            Sentry.captureMessage('Driver PII tracking - sending events', {
              level: 'info',
              extra: {
                policyId: policy.id,
                webUserId: policy.webUserId,
                driversCount: driversWithPIIChanges.length,
                drivers: driversWithPIIChanges.map(({ driver, changes }) => ({
                  name: `${driver.firstName} ${driver.lastName}`,
                  updatedDOB: changes.dateOfBirth,
                  updatedDL: changes.driversLicenseNumber
                }))
              }
            });

            // Check if webUserId is missing
            if (!policy.webUserId) {
              Sentry.captureMessage('Driver PII tracking failed - missing webUserId', {
                level: 'warning',
                extra: {
                  policyId: policy.id,
                  driversCount: driversWithPIIChanges.length
                }
              });
            } else {
              for (const { driver, changes } of driversWithPIIChanges) {
                try {
                  const result = await sendEventAsMember({
                    eventName: 'Driver PII Submitted',
                    userId: policy.webUserId,
                    properties: JSON.stringify({
                      driverName: `${driver.firstName} ${driver.lastName}`,
                      policyId: policy.id,
                      updatedDateOfBirth: changes.dateOfBirth,
                      updatedDriversLicenseNumber: changes.driversLicenseNumber
                    })
                  });

                  // Log if the event sending failed
                  if (result?.error) {
                    Sentry.captureException(new Error('Driver PII event sending failed'), {
                      extra: {
                        policyId: policy.id,
                        webUserId: policy.webUserId,
                        driverName: `${driver.firstName} ${driver.lastName}`,
                        error: result.error
                      }
                    });
                  }
                } catch (error) {
                  Sentry.captureException(error, {
                    extra: {
                      context: 'Driver PII event sending',
                      policyId: policy.id,
                      webUserId: policy.webUserId,
                      driverName: `${driver.firstName} ${driver.lastName}`
                    }
                  });
                }
              }
            }
          }
        }
      } else {
        let message = res.error?.message;
        if (!isNaN(+res.error?.message)) {
          // Only use getErrorData if we're confident this is an actual error code
          const errorCode = parseInt(res.error.message, 10);
          const errorData = errorCode ? getErrorData({ error: { code: errorCode }, state: policy.state }) : null;
          message = (errorData?.policyErrorMessage || errorData?.why) ?? res.error.message;
        }
        handleError(
          {
            error: {
              message,
              tryAgain: handleSegmentChange.bind(this, segment, changeType, initialValues)
            }
          },
          { revisedSegment }
        );
        setPreview(null);
        closePolicyModal();
      }
      setLoadingPreview(false);
    },
    [policy, addSegment, changeSegment]
  );

  const handleChangeTab = useCallback(
    (e, tab) => {
      if ((changed && !disableLeavePageModal) || previewIdFromQueryParams) {
        e.preventDefault();
        setPromptTab(tab);
      } else {
        setCurrentTab(tab);
      }
      // used for appcues to work when needed, do not need for the settings tab
      if (tab !== PolicyTabs.SETTINGS) {
        track(`Staff Appcues: ${tab} Tab Clicked`, {
          isAgency,
          affinityCode: user.affinityCode
        });
      }
    },
    [changed, setCurrentTab, previewIdFromQueryParams, disableLeavePageModal]
  );

  const handlePreviewPolicyChangeScrubIncidents = useCallback(
    async (policyValue, incidents) => {
      setLoadingState();
      const res = await previewPolicyChangeScrubIncidents(policyValue, incidents);
      if (res.ok) {
        const { preview } = res;
        setPreview(preview);
      } else {
        handleError(
          { ...res, tryAgain: handlePreviewPolicyChangeScrubIncidents.bind(this, policyValue, incidents) },
          { policyValue }
        );
      }
      setLoadingPreview(false);
    },
    [previewPolicyChangeScrubIncidents]
  );

  const handleConfirmChanges = async ({
    previewId,
    notes,
    policyPreviewUsername,
    mailingAddress: incomingMailingAddress
  }) => {
    const hasChangesThatNeedApproval = changesForApprovalConfig?.hasChangesThatNeedApproval;
    const approvingChangesRequestedByIA = hasChangesThatNeedApproval && preview?.createdBy.isAgency;

    const mailingAddressChanged = !isEqual(incomingMailingAddress, mailingAddress);

    if (action === 'cancel' && mailingAddressChanged) {
      const { data } = await updateAccount({
        id: accountId,
        fname: fname,
        lname: lname,
        email: email,
        phoneNumber: phoneNumber,
        mailingAddress: incomingMailingAddress
      });
      if (!data) {
        toast.notify({
          type: 'error',
          message:
            'Failed to update mailing address. Please try again. If the problem persists, please contact support.',
          label: 'Edit in Account Details',
          action: () => {
            history.push(`/customer/${accountId}?scrollTo=account-details`, { openAccountCard: true });
          }
        });
        return; // Don't proceed with cancellation if address update failed
      }
    }

    setLoadingState();
    let res = await confirm(policy.id, previewId, notes);
    let hasError = !res.ok;
    if (res.ok) {
      if (hasChangesThatNeedApproval) {
        track('Change request - Approved', {
          isAgency,
          username: user.username
        });
      }

      res = await getRenewalPolicyId(policy.id);

      // If approving changes requested by IA, send a communication to the agent
      if (approvingChangesRequestedByIA) {
        await sendApprovedRequestedChangesCommunication(policy.id, policyPreviewUsername);
      }

      if (previewIdFromQueryParams) {
        removeQueryParamsFromUrl(history);
      }

      hasError = !res.ok;
      if (res.ok) {
        const { renewalPolicyId } = res;
        setActivePolicyModal(renewalPolicyId ? PolicyModalTypes.RENEWAL : null);
        setRenewalPolicyId(renewalPolicyId);
        toast.notify({
          type: 'success',
          message: 'The policy was updated successfully',
          label: "Go to Member's Details",
          action: () => {
            history.push(`/customer/${accountId}`);
          }
        });

        fetchFullAccountAndPolicy(policy.id, accountId);
      }
    }
    if (hasError) {
      handleError(
        {
          error: {
            message: friendlyErrorMessage(res.error.message),
            title: 'Confirmation error',
            label: 'Refresh page'
          }
        },
        { id: previewId, notes }
      );
    }
    setLoadingPreview(false);
  };

  const handleSubmitChangesForApproval = async ({ policyChangesDescription, previewId }) => {
    const policyPreviewLink = `${window.location.href.split('?')[0]}?previewId=${previewId}`;
    setLoadingState();
    const res = await submitChangesForApproval(policy.id, policyPreviewLink, policyChangesDescription || '');

    if (res.ok) {
      setActivePolicyModal(PolicyModalTypes.SUBMITTED_FOR_APPROVAL);

      track('Change request - Submitted', {
        isAgency,
        username: user.username
      });
    } else {
      handleError(
        {
          error: {
            message: friendlyErrorMessage(res.error.message),
            title: 'Confirmation error',
            label: 'Refresh page'
          }
        },
        { id: policy.id, notes: policyChangesDescription || '' }
      );
    }

    setLoadingPreview(false);
  };

  const remountComponent = () => {
    setRemountKey((prev) => prev + 1);
  };

  return (
    <DisableProvider key={remountKey}>
      {!isLoading && notifications && <PolicyNotifications onClick={() => setCurrentTab(PolicyTabs.DETAILS)} />}
      {session.canViewPolicies && (
        <Tabs value={currentTab} variant="fullWidth" onChange={handleChangeTab}>
          <Tab label="Settings" className={classes.tab} value={PolicyTabs.SETTINGS} />
          <Tab label="Payment & Price" className={classes.tab} value={PolicyTabs.PAYMENT} />
          <Tab label="Details" className={classes.tab} value={PolicyTabs.DETAILS} />
          <Tab label="Documents" className={classes.tab} value={PolicyTabs.DOCUMENTS} />
          <Tab label="Incidents" className={classes.tab} value={PolicyTabs.INCIDENTS} />
        </Tabs>
      )}
      {/* Check styles for Section */}
      <Section className={classNames([classes.details, { [classes.withFooter]: changed }])}>
        {isLoading ? (
          <Loading type="secondary" />
        ) : (
          <>
            {currentTab === PolicyTabs.SETTINGS && session.canViewPolicies && (
              <PolicySettings
                key={`version-${policy.version}`}
                policy={policy}
                accountId={accountId}
                loadingPreview={loadingPreview}
                onPreviewPolicy={handleChangePolicy}
                goToPaymentTab={() => setCurrentTab(PolicyTabs.PAYMENT)}
                cancelledOrInCancellation={cancelledOrInCancellation}
                allowReinstatement={allowReinstatement}
                allowRescind={allowRescind}
                isCancelledGSNICPolicy={isCancelledGSNICPolicy}
                disableLeavePageModal={disableLeavePageModal}
              />
            )}
            {currentTab === PolicyTabs.PAYMENT && (
              <PaymentTab loadingPreview={loadingPreview} handleChangePolicy={handleChangePolicy} />
            )}
            {currentTab === PolicyTabs.DETAILS && (
              <PolicyDetails
                key={`version-${policy.version}`}
                loadingPreview={loadingPreview}
                onSegmentPreview={handleSegmentChange}
                repEmail={session.user.email}
                onClose={closeModal}
                preview={preview}
              />
            )}
            {(currentTab === PolicyTabs.DOCUMENTS || (!session.canEditPolicies && !session.viewOnly)) && (
              <Documents showRecreateApplication={session.canEditPolicies} />
            )}
            {currentTab === PolicyTabs.INCIDENTS && (
              <Incidents loadingPreview={loadingPreview} handleChangePolicy={previewPolicyChangeScrubIncidents} />
            )}
          </>
        )}
      </Section>

      {activePolicyModal === PolicyModalTypes.PREVIEW && (
        <Preview
          open={activePolicyModal === PolicyModalTypes.PREVIEW}
          preview={preview}
          onClose={closeModal}
          onConfirm={
            changesForApprovalConfig?.hasChangesThatNeedApproval && !canApproveRequestedPIPChanges
              ? handleSubmitChangesForApproval
              : handleConfirmChanges
          }
          loading={loadingPreview}
          action={action}
          policyType={policy.policyType}
          cancelReason={reasons.cancelReason}
          nonRenewReason={reasons.nonRenewReason}
          isAgency={session.isAgency}
          changesForApprovalConfig={changesForApprovalConfig}
        />
      )}
      {activePolicyModal === PolicyModalTypes.RENEWAL && (
        <RenewalNotice
          open={activePolicyModal === PolicyModalTypes.RENEWAL}
          onClose={closeModal}
          renewalPolicyId={renewalPolicyId}
        />
      )}
      {activePolicyModal === PolicyModalTypes.WARNING && (
        <WarningModal
          open={activePolicyModal === PolicyModalTypes.WARNING}
          onClose={() => {
            setActivePolicyModal(PolicyModalTypes.PREVIEW);
          }}
          reason={preview.warning}
        />
      )}
      {activePolicyModal === PolicyModalTypes.SUBMITTED_FOR_APPROVAL && (
        <SubmittedForApprovalModal
          open={activePolicyModal === PolicyModalTypes.SUBMITTED_FOR_APPROVAL}
          onClose={() => {
            closeModal();
            // remount the component to reset all forms
            remountComponent();
          }}
        />
      )}
      {activePolicyModal === PolicyModalTypes.PREVIEW_LINK_EXPIRED && (
        <PolicyPreviewExpiredModal
          open={activePolicyModal === PolicyModalTypes.PREVIEW_LINK_EXPIRED}
          onClose={closeModal}
        />
      )}
      <LeavePageDialog
        message={
          previewIdFromQueryParams
            ? "Preview with requested changes will be closed. You'll be able to reopen it by following the preview link again. Are you sure you want to want to close it?"
            : undefined
        }
        title={previewIdFromQueryParams ? 'Close Preview' : undefined}
        onClose={() => setPromptTab(null)}
        open={!!promptTab}
        cb={() => {
          setCurrentTab(promptTab);
          setPromptTab(null);
          setChanged(false);
        }}
      />
      <ErrorDialog
        open={!!error.messages}
        errors={error.messages}
        onClose={() => setError({})}
        label={error.label}
        onClick={error.tryAgain || error.onClick}
        title={error.title}
        showGoHomeButton={!previewIdFromQueryParams}
      />
      {currentTab !== PolicyTabs.SETTINGS && cancelled && (
        <CancelledPolicyFooter policyStatus={policyStatus} isAgency={session.isAgency} policy={policy} />
      )}
    </DisableProvider>
  );
};

Policy.propTypes = {
  history: PropTypes.object.isRequired
};

export default observer(Policy);
